import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Smartphone, Loader2, Wallet } from "lucide-react";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function BalancePay({ booking, contact, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [utr, setUtr] = useState("");
  const [loading, setLoading] = useState(false);
  const bal = booking.balance_upi;
  if (!bal) return null;

  const submitted = booking.balance_payment_status === "submitted";

  const submit = async () => {
    if (!utr.trim()) return toast.error("Enter the UPI reference / transaction ID");
    setLoading(true);
    try {
      await api.post(`/bookings/${booking.booking_number}/balance-ref`, { utr, contact });
      toast.success("Balance payment reference submitted");
      setOpen(false);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return <div className="mt-3 text-sm rounded-lg bg-amber-500/10 text-amber-400 p-3" data-testid="balance-submitted">Balance payment reference received — awaiting verification.</div>;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`pay-balance-btn-${booking.booking_number}`} className="mt-3 gold-gradient text-white font-semibold rounded-full">
          <Wallet size={16} className="mr-2" /> Pay Balance {fmtINR(bal.amount)}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-pink-200 text-zinc-900 max-w-md">
        <DialogHeader><DialogTitle className="font-display text-xl">Pay Balance via UPI</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-600">Pay exactly <span className="text-gold font-semibold">{fmtINR(bal.amount)}</span> and enter your UPI reference.</p>
        <div className="bg-white rounded-xl p-3 flex justify-center my-3">
          <img src={bal.qr_url} alt="UPI QR" className="w-48 rounded-lg" data-testid="balance-qr" />
        </div>
        <div className="rounded-xl bg-white border border-pink-200 p-3 flex items-center justify-between">
          <span className="text-gold font-mono text-sm">{bal.upi_id}</span>
          <button onClick={() => { navigator.clipboard.writeText(bal.upi_id); toast.success("UPI ID copied"); }} className="text-zinc-600 hover:text-gold"><Copy size={15} /></button>
        </div>
        <a href={bal.upi_link}><Button variant="outline" className="w-full mt-3 border-gold/40 text-gold hover:bg-gold/10"><Smartphone size={16} className="mr-2" /> Pay via UPI App</Button></a>
        <Label className="text-zinc-700 mt-3">UPI Reference / Transaction ID *</Label>
        <Input value={utr} data-testid="balance-utr-input" onChange={(e) => setUtr(e.target.value)} className="mt-1 bg-white border-pink-200 text-zinc-900" placeholder="e.g. 4312XXXXXXXX" />
        <Button onClick={submit} disabled={loading} data-testid="submit-balance-ref-btn" className="w-full mt-3 gold-gradient text-white font-semibold rounded-full">
          {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Submit
        </Button>
      </DialogContent>
    </Dialog>
  );
}
